import { Box, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DealType } from "src/business/deal";
import { useGetRestaurantDeals } from "src/data/deals/queries/deals";
import { InfoCard } from "src/design";
import { colorTextMuted } from "src/design/tokens";
import { CardLink } from "./CardLink";
import { OfferRow } from "./OfferRow";

/** Active offers lead the list; the rest are one click away on Deals. */
const MAX_ROWS = 4;

interface OffersCardProps {
  restaurantId: string;
}

export const OffersCard = ({ restaurantId }: OffersCardProps) => {
  const { t } = useTranslation();
  const { data: deals = [], isLoading } = useGetRestaurantDeals({
    restaurantId,
  });

  const activeDeals = deals.filter(
    (deal) => deal.active && deal.type === DealType.DEAL,
  ).length;
  const activeEvents = deals.filter(
    (deal) => deal.active && deal.type === DealType.EVENT,
  ).length;

  // Active offers first — an inactive one is not driving covers today.
  const rows = [...deals]
    .sort((left, right) => Number(right.active) - Number(left.active))
    .slice(0, MAX_ROWS);

  return (
    <InfoCard
      title={t("dashboard.offers.title")}
      subtitle={t("dashboard.offers.subtitle", {
        deals: activeDeals,
        events: activeEvents,
        total: deals.length,
      })}
      action={
        <CardLink
          to={`/restaurant/${restaurantId}/deals`}
          label={t("dashboard.manage")}
        />
      }
      sx={{ height: "100%" }}
    >
      {isLoading ? (
        <Box>
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={48}
              sx={{ mb: 1.5 }}
            />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ fontSize: "14px", color: colorTextMuted, py: 2 }}>
          {t("dashboard.offers.empty")}
        </Typography>
      ) : (
        rows.map((deal, index) => (
          <OfferRow
            key={deal.id}
            deal={deal}
            isLast={index === rows.length - 1}
          />
        ))
      )}
    </InfoCard>
  );
};
